import { Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { Calculator, ClipboardList, Calendar, CheckSquare, FolderOpen, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const ROLE_HIERARCHY = ['Trainee', 'Staff', 'Supervisor', 'Manager', 'Admin'];

function canAccess(userRole: string | undefined, minRole: string | null) {
  if (!minRole) return true;
  const ui = Math.max(ROLE_HIERARCHY.indexOf(userRole || 'Staff'), 0);
  const mi = ROLE_HIERARCHY.indexOf(minRole);
  return ui >= mi;
}

const TOOLS = [
  {
    title: 'GP Calculator', description: 'Calculate gross profit for beer, wine, spirits, cocktails and more. Save, print and share specs.',
    icon: Calculator, href: '/gp-calculator', accent: 'text-blue-500', bg: 'bg-blue-500/10',
    minRole: null, tags: 'Beer · Wine · Spirit · Cocktail · General',
  },
  {
    title: 'Shift Prep Builder', description: 'Build a complete shift prep sheet with weather, events, staff plan and printable checklist.',
    icon: ClipboardList, href: '/shift-prep', accent: 'text-emerald-500', bg: 'bg-emerald-500/10',
    minRole: 'Supervisor', tags: 'Weather · Bookings · Staff · Checklist',
  },
  {
    title: 'Event Setup Builder', description: 'Create event setup checklists, run sheets and staff briefings for any event at The Rattlebone.',
    icon: Calendar, href: '/event-setup', accent: 'text-purple-500', bg: 'bg-purple-500/10',
    minRole: 'Supervisor', tags: 'Run Sheet · Setup · Clear-down',
  },
  {
    title: 'Checklists', description: 'Opening, closing, bar, fire, kitchen and custom checklists — complete and track them digitally.',
    icon: CheckSquare, href: '/checklists', accent: 'text-amber-500', bg: 'bg-amber-500/10',
    minRole: null, tags: 'Opening · Bar · Fire · Cleaning · Cellar',
  },
  {
    title: 'My Tools & Documents', description: 'View all your saved calculations, shift preps, event sheets and completed checklists in one place.',
    icon: FolderOpen, href: '/my-tools', accent: 'text-primary', bg: 'bg-primary/10',
    minRole: null, tags: 'All saved items',
  },
];

export default function Tools() {
  const { user } = useAuth();

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Tools & Shift Control</h1>
        <p className="text-muted-foreground mt-1.5">Practical operational tools for shift prep, event management, GP calculations and daily operations.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {TOOLS.map(tool => {
          const accessible = canAccess(user?.role, tool.minRole);
          const Icon = tool.icon;
          return (
            <Card key={tool.title} className={`p-5 transition-all ${accessible ? 'hover:border-primary/50 hover:shadow-md cursor-pointer' : 'opacity-50'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${tool.bg} shrink-0`}>
                  <Icon className={`w-6 h-6 ${tool.accent}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-semibold text-base">{tool.title}</h2>
                    {tool.minRole && <Badge variant="outline" className="text-xs">{tool.minRole}+</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{tool.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{tool.tags}</span>
                    {accessible && (
                      <Link to={tool.href} className="flex items-center gap-1 text-sm font-medium text-primary hover:underline shrink-0">
                        Open <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
